import {
  Badge,
  Button,
  Card,
  Input,
  Popover,
  Select,
  Space,
  Table,
  Typography,
  Tag,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { CardSchema, CardStatus } from '../../types/cards/card-types';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { formatDate } from '../../utils/format';
import { ColumnsType } from 'antd/es/table';
import { FilterOutlined } from '@ant-design/icons';
import InfiniteLoaderWrapper from '../InfiniteLoaderWrapper';

type TCardDataProps = {
  cardData: CardSchema[];
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<any[], Error>>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  handleApplyFilter: (status: string, type: string) => void;
  isTableLoading: boolean;
  handleRefresh: () => void;
  isRefreshLoadingButton: boolean;
};

const getStatusConfig = (status: CardStatus) => {
  const config = {
    'pending-disburse': {
      color: 'processing',
      text: 'PENDING DISBURSE',
    },
    'completed': {
      color: 'success',
      text: 'COMPLETED',
    },
    'pending-funding': {
      color: 'warning',
      text: 'PENDING FUNDING',
    },
    'requested': {
      color: 'default',
      text: 'REQUESTED',
    },
    'issued': {
      color: 'processing',
      text: 'ISSUED',
    },
    'pending-collection': {
      color: 'warning',
      text: 'PENDING COLLECTION',
    },
    'dispatched': {
      color: 'success',
      text: 'DISPATCHED',
    },
  };

  return config[status] || {
    color: 'default',
    text: status.toUpperCase(),
  };
};

function CardsList(props: TCardDataProps) {
  // Table columns (listing only – no actions)
  const allCardsColumn: ColumnsType<CardSchema> = [
    {
      title: 'Card Id',
      dataIndex: 'id',
      key: 'id',
      width: 150,
    },
    {
      title: 'Name',
      dataIndex: 'holder_name',
      key: 'holder_name',
      width: 200,
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      width: 200,
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 150,
    },
    {
      title: 'Type',
      key: 'card_type',
      width: 150,
      render: (_: unknown, record: CardSchema) => {
        const type = (record.type ?? record.card_type ?? '') as string;
        return (
          <span style={{ textTransform: 'capitalize' }}>
            {type || '—'}
          </span>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 150,
      render: (date: number) => formatDate(date),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: CardStatus) => {
        const config = getStatusConfig(status);
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
  ];

  const {
    cardData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    handleApplyFilter,
    isTableLoading,
    handleRefresh,
    isRefreshLoadingButton,
  } = props;
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredData, setFilteredData] = useState<CardSchema[]>([]);
  const [filter, setFilter] = useState({
    status: '',
    type: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);

    // Filter card item based on the search value
    const filteredData = cardData.filter((item) =>
      item.id.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setFilteredData(filteredData);
  };

  const handleStatusChange = (val: string) => {
    setFilter({ ...filter, status: val });
  };

  const handleTypeChange = (val: string) => {
    setFilter({ ...filter, type: val });
  };

  useEffect(() => {
    if (cardData) {
      setFilteredData(cardData);
    }
  }, [cardData]);

  const content = (
    <Space direction='vertical' style={{ width: '100%' }}>
      <Space direction='vertical' style={{ width: '100%' }}>
        <Typography.Text>Status:</Typography.Text>
        <Select
          value={filter.status}
          style={{ width: '100%' }}
          onChange={handleStatusChange}
          placeholder='Status'
          optionFilterProp='label'
          options={[
            { value: 'requested', label: 'Requested' },
            { value: 'issued', label: 'Issued' },
            { value: 'pending-collection', label: 'Pending Collection' },
            { value: 'pending-disburse', label: 'Pending Disburse' },
            { value: 'completed', label: 'Completed' },
            { value: 'pending-funding', label: 'Pending Funding' },
            { value: 'dispatched', label: 'Dispatched' },
          ]}
        />
      </Space>
      <Space direction='vertical' style={{ width: '100%' }}>
        <Typography.Text>Type:</Typography.Text>
        <Select
          value={filter.type}
          style={{ width: '100%' }}
          onChange={handleTypeChange}
          placeholder='Type'
          options={[
            { value: 'virtual', label: 'Virtual' },
            { value: 'physical', label: 'Physical' },
          ]}
        />
      </Space>

      <Space>
        <Button
          type='primary'
          onClick={() => handleApplyFilter(filter.status, filter.type)}
        >
          Apply
        </Button>
        <Button
          type='default'
          onClick={() => {
            setFilter({ status: '', type: '' });
          }}
        >
          Clear
        </Button>
      </Space>
    </Space>
  );

  return (
    <>
      <Card
        title={
          <Space>
            <Typography.Title level={5}>All Cards</Typography.Title>
          </Space>
        }
        style={{ marginTop: 24 }}
        extra={
          <Space>
            <Input
              placeholder='Search by id'
              value={searchValue}
              onChange={handleChange}
              style={{ width: '100%' }}
            />

            <Badge dot={true} offset={[-5, 5]} color='#1890ff'>
              <Popover
                content={content}
                title='Filter Cards by'
                trigger='click'
                placement='bottomLeft'
              >
                <Button
                  icon={<FilterOutlined />}
                  onClick={() => {}}
                  type={'default'}
                >
                  Filter{' '}
                </Button>
              </Popover>
            </Badge>

            <Button onClick={handleRefresh} loading={isRefreshLoadingButton}>
              Refresh
            </Button>
          </Space>
        }
      >
        <InfiniteLoaderWrapper
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        >
          <Table
            dataSource={filteredData}
            columns={allCardsColumn}
            rowKey='id'
            pagination={false}
            loading={isTableLoading}
          />
        </InfiniteLoaderWrapper>
      </Card>
    </>
  );
}

export default CardsList;
